import { useState, useEffect } from 'react'
import { MapPin, Loader2 } from '@/ui/icons'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Sheet,
  SheetContent,
  SheetBody
} from '@/ui/components'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Label } from '@/ui/components'
import { TripsApi } from '@/shared/api/trips'
import { useAuth } from '@/modules/auth'
import { parseCoordinates } from '../../utils/meetupWaypointSync'
import type { CreateWaypointDto } from '@/shared/api/routes'
import type { POIType } from '@/pages/itinerary-builder/utils/waypointTypeStyles'
import { generateWaypointName } from '../../utils/waypointNameGenerator'

interface AddWaypointDialogProps {
  isOpen: boolean
  onClose: () => void
  tripId: string
  onWaypointAdded?: () => void
  initialPoiType?: POIType | null
  initialTripDay?: number | null
  initialCustomLabel?: string | null
}

const MOBILE_BREAKPOINT = 767

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const update = () => setIsMobile(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return isMobile
}

export function AddWaypointDialog({
  isOpen,
  onClose,
  tripId,
  onWaypointAdded,
  initialPoiType,
  initialTripDay,
  initialCustomLabel
}: AddWaypointDialogProps) {
  const { getAccessToken } = useAuth()
  const isMobile = useIsMobile()
  const [coordinates, setCoordinates] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Reset form when dialog/sheet opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setCoordinates('')
      setError(null)
      onClose()
    }
  }

  const handleSave = async () => {
    // Clear previous errors
    setError(null)

    // Validate input is not empty
    if (!coordinates.trim()) {
      setError('Please enter coordinates')
      return
    }

    // Parse coordinates
    const parsed = parseCoordinates(coordinates.trim())
    if (!parsed) {
      setError('Invalid coordinate format. Please use "latitude, longitude" (e.g., "37.7749, -122.4194")')
      return
    }

    // Validate ranges (parseCoordinates already does this, but double-check)
    if (parsed.latitude < -90 || parsed.latitude > 90) {
      setError('Latitude must be between -90 and 90')
      return
    }

    if (parsed.longitude < -180 || parsed.longitude > 180) {
      setError('Longitude must be between -180 and 180')
      return
    }

    setIsSaving(true)

    try {
      const apiClient = new TripsApi(getAccessToken)
      
      // Generate smart waypoint name based on POI type
      const waypointName = generateWaypointName(
        initialPoiType ?? null,
        initialCustomLabel ?? null
      )

      // Create waypoint data
      // Note: trip_id and route_id are not included - waypoints are associated with trips via trip_waypoints junction table
      // route_id was deprecated - waypoints are now trip-level, not route-level
      const waypointData: CreateWaypointDto = {
        name: waypointName,
        latitude: parsed.latitude,
        longitude: parsed.longitude,
        source: 'manual',
        trip_id: tripId,
        trip_day: initialTripDay ?? undefined,
      }
      
      // Only set poi_type if it was explicitly provided
      // - null = Route Stop (explicitly selected)
      // - string = Detour type (e.g., 'viewpoint', 'campsite')
      // - undefined = not set (shouldn't happen if user selected from dropdown)
      if (initialPoiType !== undefined) {
        waypointData.poi_type = initialPoiType
      } else {
        // If not provided, default to null (Route Stop)
        waypointData.poi_type = null
      }
      
      // Set custom label if provided (for custom types like "Lunch Stop", "Overlook")
      if (initialCustomLabel) {
        waypointData.poi_custom_label = initialCustomLabel
      }

      await apiClient.addWaypoint(tripId, waypointData)

      // Success

      // Reset form
      setCoordinates('')
      setError(null)
      
      // Close dialog
      onClose()
      
      // Refresh waypoints with cache-busting to ensure we get the new waypoint
      // Add a small delay to ensure the backend has processed the waypoint creation
      if (onWaypointAdded) {
        setTimeout(() => {
          onWaypointAdded()
        }, 200)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add waypoint. Please try again.'

      setError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSaving) {
      handleSave()
    }
  }

  const formFields = (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="coordinates">Coordinates</Label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="coordinates"
            type="text"
            placeholder="37.7749, -122.4194"
            value={coordinates}
            onChange={(e) => {
              setCoordinates(e.target.value)
              setError(null)
            }}
            onKeyDown={handleKeyDown}
            className="pl-10 min-h-[44px]"
            disabled={isSaving}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? 'coordinates-error' : undefined}
          />
        </div>
        {error && (
          <p id="coordinates-error" className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Latitude: -90 to 90, Longitude: -180 to 180
        </p>
      </div>
    </div>
  )

  const footerButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        disabled={isSaving}
        className="min-h-[44px]"
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={handleSave}
        disabled={isSaving || !coordinates.trim()}
        className="min-h-[44px]"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Adding...
          </>
        ) : (
          'Add Waypoint'
        )}
      </Button>
    </>
  )

  // Mobile: bottom drawer (Sheet) — coordinates field and actions only, no title/description
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent side="bottom" className="p-0 gap-0 flex flex-col max-h-[90dvh]">
          <div className="flex-shrink-0 flex justify-center pt-2 pb-1" aria-hidden>
            <div className="w-10 h-1 rounded-full bg-earth-300" />
          </div>
          <SheetBody className="flex-1 overflow-y-auto px-4 pt-2 pb-4">
            {formFields}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              {footerButtons}
            </div>
          </SheetBody>
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop: modal (Dialog)
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-earth-50 border-earth-700 text-foreground">
        <DialogHeader>
          <DialogTitle className="text-foreground">Add Waypoint</DialogTitle>
          <DialogDescription>
            Enter coordinates in the format "latitude, longitude" (e.g., "37.7749, -122.4194")
          </DialogDescription>
        </DialogHeader>
        {formFields}
        <DialogFooter>{footerButtons}</DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

